import { DashboardRoom, ScheduledSessionOfUser, useAppContext } from '@/AppContext'
import useAppContextRef from '@/services/useAppContextRef'
import { useResource } from '@/services/useResource'
import { AppEvents, useUserContext } from '@/UserContext'
import { Trigger } from '@/utlis/Trigger'
import { useEffect } from 'react'
import { logger } from '@/services/Logger'
import { useHistory } from 'react-router-dom'
import { persistent } from '@/services/LocalStorage'

export type NewsCard = {
    id: string
    cardTitle?: string
    messageTitle: string
    message: string
    callToActionText?: string
    callToActionLink?: string
    imageUrl?: string
    imageLinkUrl?: string
    createdAt: Date
    expiresAt: Date
}

export type ApiFrontPageResponse = {
    scheduledSessions: ScheduledSessionOfUser[]
    recentRooms: DashboardRoom[]
    permanentRooms: DashboardRoom[]
    events: AppEvents[]
    newsCards: NewsCard[]
}

export const frontpageReloadTrigger = new Trigger<void>()

const log = logger.createChildLogger({ name: 'useFrontPage' })

/**
 * Loads "frontpage" data from the API. This includes basic user info, recent
 * rooms, scheduled sessions, notifications, etc.
 *
 * Use `frontpageReloadTrigger.trigger()` to reload frontpage data from anywhere
 * within the app.
 */
export function useFrontpage(): { frontpage: ApiFrontPageResponse | null; frontpageLoading: boolean } {
    const [_userState, userDispatch] = useUserContext()
    const [_appState, appDispatch] = useAppContext()
    const { appStateRef } = useAppContextRef()

    const history = useHistory()

    const {
        data: frontpage,
        loading: frontpageLoading,
        refetch,
    } = useResource<ApiFrontPageResponse>('/v1/users/_me?view=front-page')

    useEffect(() => {
        const handler = () => {
            refetch()
        }

        frontpageReloadTrigger.on('trigger', handler)
        return () => {
            frontpageReloadTrigger.off('trigger', handler)
        }
    }, [refetch])

    useEffect(() => {
        if (frontpage) {
            persistent.set('scheduledSessions', frontpage.scheduledSessions)
            persistent.set('recentRooms', frontpage.recentRooms)
            persistent.set('permanentRooms', frontpage.permanentRooms)
            userDispatch({
                type: 'set_app_events',
                value: frontpage.events || [],
            })
        }
    }, [frontpage, userDispatch])

    return { frontpage, frontpageLoading }
}
